import { NSABoxSecret, NSARouterMakerResult } from "namirasoft-account-react";
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { ProviderDelayUnit, ProviderLimitPerUnit, ProviderRow, ProviderType } from "namirasoft-sms-sender";
import React, { createRef } from "react";
import { SMSSenderEntities } from "../SMSSenderEntities";
import { SMSSenderLayout } from "../SMSSenderLayout";
import { SMSSenderProps } from "../SMSSenderProps";

export interface ProviderEditPageProps extends NSARouterMakerResult, SMSSenderProps
{
    isEdit: boolean
}

export default class ProviderEditPage extends React.Component<ProviderEditPageProps>
{
    private NSBoxString_Name = createRef<NSBoxString>();
    private NSBoxEnum_Type = createRef<NSBoxEnum<ProviderType>>();
    private NSBoxString_Region = createRef<NSBoxString>();
    private NSABoxSecret_AccessKey = createRef<NSABoxSecret>();
    private NSABoxSecret_SecretKey = createRef<NSABoxSecret>();
    private NSBoxBoolean_Limit = createRef<NSBoxBoolean>();
    private NSBoxInteger_Limit_Count = createRef<NSBoxInteger>();
    private NSBoxInteger_Limit_Per_Value = createRef<NSBoxInteger>();
    private NSBoxEnum_Limit_Per_Unit = createRef<NSBoxEnum<ProviderLimitPerUnit>>();
    private NSBoxBoolean_Delay = createRef<NSBoxBoolean>();
    private NSBoxInteger_Delay_Value = createRef<NSBoxInteger>();
    private NSBoxEnum_Delay_Unit = createRef<NSBoxEnum<ProviderDelayUnit>>();
    private NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: ProviderEditPageProps)
    {
        super(props);
        this.state = {};
    }
    override render()
    {
        let extra_tabs = [];
        extra_tabs.push(
            {
                title: "Limit",
                clicked: true,
                getContent: () =>
                {
                    return (
                        <NSColumn style={{ width: NSBox.width.double }}>
                            <NSRow>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Limit}
                                    title="Limit"
                                    required
                                    style={{ width: "100%" }}
                                    defaultValue={false}
                                    onChanged={() => this.setState({})}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-limit" }}
                                />
                            </NSRow>
                            <NSRow style={this.NSBoxBoolean_Limit.current?.getValueBool() ? {} : { display: "none" }}>
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Limit_Count}
                                    title="Limit Count"
                                    required
                                    min={0}
                                    style={{ width: "100%" }}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-limit-count" }}
                                />
                            </NSRow>
                            <NSRow style={this.NSBoxBoolean_Limit.current?.getValueBool() ? {} : { display: "none" }}>
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Limit_Per_Value}
                                    title="Limit Per Value"
                                    required
                                    min={1}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-limit-per-value" }}
                                />
                                <NSBoxEnum<ProviderLimitPerUnit>
                                    ref={this.NSBoxEnum_Limit_Per_Unit}
                                    title="Limit Per Unit"
                                    required
                                    multiple={false}
                                    getEnumObject={() => ProviderLimitPerUnit}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-limit-per-unit" }}
                                />
                            </NSRow>
                        </NSColumn>
                    );
                }
            },
            {
                title: "Delay",
                clicked: true,
                getContent: () =>
                {
                    return (
                        <NSColumn style={{ width: NSBox.width.double }}>
                            <NSRow>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Delay}
                                    title="Delay"
                                    required
                                    style={{ width: "100%" }}
                                    defaultValue={false}
                                    onChanged={() => this.setState({})}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-delay" }}
                                />
                            </NSRow>
                            <NSRow style={this.NSBoxBoolean_Delay.current?.getValueBool() ? {} : { display: "none" }}>
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Delay_Value}
                                    title="Delay Value"
                                    required
                                    min={1}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-delay-value" }}
                                />
                                <NSBoxEnum<ProviderDelayUnit>
                                    ref={this.NSBoxEnum_Delay_Unit}
                                    title="Delay Unit"
                                    required
                                    multiple={false}
                                    getEnumObject={() => ProviderDelayUnit}
                                    info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-delay-unit" }}
                                />
                            </NSRow>
                        </NSColumn>
                    );
                }
            }
        );

        return (
            <SMSSenderLayout<ProviderRow, ProviderRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to update the details of an existing SMS provider." : " This section allows you to enter information to create a provider so you can send messages. Click the info icon for more information about each field.",
                }}
                pages={{
                    entity: SMSSenderEntities.provider(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ProviderType, ProviderType.AWS) ?? ProviderType.AWS;
                                        let meta = null;
                                        if (type === ProviderType.AWS)
                                        {
                                            let region = this.NSBoxString_Region.current?.getValue() ?? "";
                                            let access_key = this.NSABoxSecret_AccessKey.current?.getValue() ?? "";
                                            let secret_key = this.NSABoxSecret_SecretKey.current?.getValue() ?? "";
                                            meta = { region, access_key, secret_key };
                                        }

                                        // Limit
                                        let limit: boolean = this.NSBoxBoolean_Limit.current?.getValueBool() ?? false;
                                        let limit_count: number | null = null;
                                        let limit_per_value: number | null = null;
                                        let limit_per_unit: ProviderLimitPerUnit | null = null;
                                        if (limit)
                                        {
                                            limit_count = this.NSBoxInteger_Limit_Count.current?.getValue() ?? 0;
                                            limit_per_value = this.NSBoxInteger_Limit_Per_Value.current?.getValue() ?? 0;
                                            limit_per_unit = this.NSBoxEnum_Limit_Per_Unit.current?.getValueEnumOne(ProviderLimitPerUnit, ProviderLimitPerUnit.Second) ?? ProviderLimitPerUnit.Second;
                                        }

                                        // Delay
                                        let delay: boolean = this.NSBoxBoolean_Delay.current?.getValueBool() ?? false;
                                        let delay_value: number | null = null;
                                        let delay_unit: ProviderDelayUnit | null = null;
                                        if (delay)
                                        {
                                            delay_value = this.NSBoxInteger_Delay_Value.current?.getValue() ?? 0;
                                            delay_unit = this.NSBoxEnum_Delay_Unit.current?.getValueEnumOne(ProviderDelayUnit, ProviderDelayUnit.Second) ?? ProviderDelayUnit.Second;
                                        }

                                        let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            id: "",
                                            user_id: "",
                                            workspace_id: "",

                                            name,
                                            type,
                                            meta,
                                            p_verified: true,

                                            limit,
                                            limit_count,
                                            limit_per_value,
                                            limit_per_unit,

                                            delay,
                                            delay_value,
                                            delay_unit,

                                            description,

                                            created_at: new Date(),
                                            updated_at: new Date()
                                        }
                                    },
                                    setRow: (row) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValue(row.type);
                                        if (row.type === ProviderType.AWS)
                                        {
                                            this.NSBoxString_Region.current?.setValue(row.meta.region);
                                            this.NSABoxSecret_AccessKey.current?.setValue(row.meta.access_key);
                                            this.NSABoxSecret_SecretKey.current?.setValue(row.meta.secret_key);
                                        }
                                        this.NSBoxBoolean_Limit.current?.setValue(row.limit);
                                        if (row.limit)
                                        {
                                            this.NSBoxInteger_Limit_Count.current?.setValue(row.limit_count);
                                            this.NSBoxInteger_Limit_Per_Value.current?.setValue(row.limit_per_value);
                                            this.NSBoxEnum_Limit_Per_Unit.current?.setValue(row.limit_per_unit);
                                        }
                                        this.NSBoxBoolean_Delay.current?.setValue(row.delay);
                                        if (row.delay)
                                        {
                                            this.NSBoxInteger_Delay_Value.current?.setValue(row.delay_value);
                                            this.NSBoxEnum_Delay_Unit.current?.setValue(row.delay_unit);
                                        }
                                        this.NSBoxTextArea_Description.current?.setValue(row.description ?? "");
                                    }
                                },
                                ui: {
                                    extra_tabs
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: "100%" }}
                            info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-name" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxEnum<ProviderType>
                            ref={this.NSBoxEnum_Type}
                            required
                            title="Type"
                            getEnumObject={() => ProviderType}
                            multiple={false}
                            defaultValue={ProviderType.AWS}
                            info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-type" }}
                        />
                        <NSBoxString
                            ref={this.NSBoxString_Region}
                            required
                            title="Region"
                            info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-region" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSABoxSecret
                            ref={this.NSABoxSecret_AccessKey}
                            {...this.props}
                            title="Access Key"
                            required
                            info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-access-key" }}
                        />
                        <NSABoxSecret
                            ref={this.NSABoxSecret_SecretKey}
                            {...this.props}
                            title="Secret Key"
                            required
                            info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-secret-key" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxTextArea
                            ref={this.NSBoxTextArea_Description}
                            title="Description"
                            required={false}
                            rows={5}
                            style={{ width: "100%" }}
                            info={{ link: "https://namirasoft.com/sms-sender/console-guide/provider/#field-description" }}
                        />
                    </NSRow>
                </NSColumn>
            </SMSSenderLayout>
        );
    }
}